import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import type { FileItem } from "../../types";
import type { DisplayAttribute } from "@/features/admin/panels/categories/hooks/useCategoryAttributesDisplay";
import { AttributeField } from "../upload-modal/UploadModalAttributeField";
import { useUpdateFileAttributes } from "../../hooks/useUpdateFileAttributes";
import { useEditFileAttributesForm } from "../../hooks/useEditFileAttributesForm";

interface EditFileAttributesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  file: FileItem;
  categoryAttributes: DisplayAttribute[];
  isLoading?: boolean;
  onSuccess?: () => void;
}

export function EditFileAttributesDialog({
  open,
  onOpenChange,
  file,
  categoryAttributes,
  isLoading: attributesLoading,
  onSuccess,
}: EditFileAttributesDialogProps) {
  const mutation = useUpdateFileAttributes({
    onSuccess: () => {
      onOpenChange(false);
      onSuccess?.();
    },
  });

  const {
    attributeValues,
    handleAttributeChange,
    requiredAttributes,
    optionalAttributes,
    validateAndPrepareData,
  } = useEditFileAttributesForm({
    file,
    categoryAttributes,
    open,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const attributeValuesArray = validateAndPrepareData();
    if (!attributeValuesArray) {
      return;
    }

    mutation.mutate({
      fileId: file.id,
      data: {
        attributeValues: attributeValuesArray,
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Attributes</DialogTitle>
          <DialogDescription>
            Edit the attributes of this file. All required attributes must be
            set.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {attributesLoading ? (
            <div className="py-8 text-center text-sm text-ink-500">
              Loading attributes...
            </div>
          ) : (
            <div className="space-y-4">
              {requiredAttributes.length > 0 && (
                <div className="space-y-4">
                  {requiredAttributes.map((attr) => (
                    <AttributeField
                      key={attr.id}
                      attribute={attr}
                      value={attributeValues[attr.id]}
                      isRequired
                      onChange={(value) =>
                        handleAttributeChange(attr.id, value)
                      }
                    />
                  ))}
                </div>
              )}
              {optionalAttributes.length > 0 && (
                <div className="space-y-4 pt-4 border-t border-grid-200">
                  {optionalAttributes.map((attr) => (
                    <AttributeField
                      key={attr.id}
                      attribute={attr}
                      value={attributeValues[attr.id]}
                      isRequired={attr.isRequired}
                      onChange={(value) =>
                        handleAttributeChange(attr.id, value)
                      }
                    />
                  ))}
                </div>
              )}
              {categoryAttributes.length === 0 && (
                <p className="text-sm text-ink-500">
                  No attributes configured for this category.
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending || attributesLoading}
            >
              {mutation.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
